import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion'; // AnimatePresenceをインポート
import { User, ChevronRight, Search, Plus } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';
// 【追加】Patient型をtypesからインポート
import { Patient } from '../../types';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';
import { getUnassignedFutureAppointmentsCount, subscribeAppointments } from '../../lib/api';

interface PatientListPanelProps {
  context: 'workflow' | 'settings';
  isMobile?: boolean;
}

export const PatientListPanel: React.FC<PatientListPanelProps> = ({ isMobile = false, context }) => {
  const { 
    selectedPatientId, 
    setEditingPatient,      // 【追加】
    editingPatient,         // 【追加】編集中の患者を取得
    allPatients, // 【追加】ストアから患者データを取得
    currentUser, // 【追加】現在のユーザー情報を取得
    currentClinicId,
    setShowAllPatientListPanel,
    setShowSettingsPanel,
    isNavigatingBack,
    setIsNavigatingBack,
  } = useDashboardStore();

  // 検索クエリ用のStateを追加
  const [searchQuery, setSearchQuery] = useState('');
  const [unassignedAppointmentCount, setUnassignedAppointmentCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadUnassignedWarning = async () => {
      if (context !== 'settings' || !currentClinicId) {
        if (!cancelled) setUnassignedAppointmentCount(0);
        return;
      }

      const count = await getUnassignedFutureAppointmentsCount(currentClinicId);
      if (!cancelled) {
        setUnassignedAppointmentCount(count);
      }
    };

    void loadUnassignedWarning();
    const unsubscribe = currentClinicId
      ? subscribeAppointments(() => {
          void loadUnassignedWarning();
        }, currentClinicId)
      : () => {};

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [context, currentClinicId]);

  // 【変更】ストアから患者データを取得し、検索クエリで絞り込み
  // PatientListPanelは設定コンテキストでのみ使用されるため、常にallPatientsを使用
  // Soft-delete: hide archived patients from list (history still kept for appointments/handovers)
  const basePatients = allPatients.filter(p => p.isActive !== false);

  // 検索クエリがあれば、元リストをさらに絞り込む
  const patients = searchQuery
    ? basePatients.filter(patient => {
        const lowercasedQuery = searchQuery.toLowerCase();
        const lowercasedName = patient.name.toLowerCase();
        const lowercasedNameRomaji = patient.nameRomaji.toLowerCase();
        
        // 日本語名：部分一致（従来通り）
        const nameMatch = lowercasedName.includes(lowercasedQuery);
        
        // ローマ字：頭文字から始まる検索
        const romajiMatch = lowercasedNameRomaji.startsWith(lowercasedQuery);
        
        return nameMatch || romajiMatch;
      })
    : basePatients;

  // 【重要】既存の患者カードがクリックされた時の処理
  const handlePatientSelect = (patient: Patient) => {
    // 進む動作なので、isNavigatingBackをfalseにリセット
    setIsNavigatingBack(false);
    // この関数は、編集対象の患者をストアにセットする責務だけを持つべきです
    setEditingPatient(patient);
    // スマホ版では、患者一覧パネルを閉じる
    if (isMobile && context === 'settings') {
      setShowAllPatientListPanel(false);
    }
  };

  // 【重要】「患者を追加する」ボタンがクリックされた時の処理
  const handleAddPatientClick = () => {
    // 進む動作なので、isNavigatingBackをfalseにリセット
    setIsNavigatingBack(false);
    // 編集対象はいない（null）状態で、編集パネルを開く（＝新規登録モード）
    setEditingPatient(null);
    // スマホ版では、患者一覧パネルを閉じる
    if (isMobile && context === 'settings') {
      setShowAllPatientListPanel(false);
    }
  };
  
  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%' },  // 右側から開始
        visible: { x: 0 },       // 中央に移動
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const allPatientSwipeRef = useRef<HTMLDivElement>(null);
  const handleAllPatientEdgeBack = useCallback(() => {
    setIsNavigatingBack(true);
    setShowAllPatientListPanel(false);
    setShowSettingsPanel(true);
  }, [setIsNavigatingBack, setShowAllPatientListPanel, setShowSettingsPanel]);
  const { swipeStyle: allPatientSwipeStyle } = useMobileEdgeSwipeBack(allPatientSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!(isMobile && context === 'settings'),
    onNavigateBack: handleAllPatientEdgeBack,
  });

  const content = (
    <div className="h-full flex flex-col overflow-hidden whitespace-nowrap bg-white border-r">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-2">
          {isMobile ? (
            <button
              onClick={() => {
                setIsNavigatingBack(true);
                setShowAllPatientListPanel(false);
                setShowSettingsPanel(true);
              }}
              className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
            >
              患者一覧
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">患者一覧</h2>
          )}
        </div>
      </div>
      
      {/* ▼▼▼ 【最重要】ここを修正 ▼▼▼ */}
      {/* context が 'settings' かつ 管理者権限以上の場合にのみ、検索と追加ボタンを表示する */}
      {context === 'settings' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'developer') && (
        <div className="p-4 border-b">
          {unassignedAppointmentCount > 0 && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              担当者が未設定の予約が {unassignedAppointmentCount} 件あります。患者情報を確認して対応してください。
            </div>
          )}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="患者を検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <motion.button
            onClick={handleAddPatientClick}
            className="w-full p-4 rounded-lg border border-dashed border-green-200 bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
          >
            <div className="flex items-center justify-center space-x-3">
              <Plus size={20} />
              <span className="font-medium">患者を追加する</span>
            </div>
          </motion.button>
        </div>
      )}
      {/* ▲▲▲ ここまで修正 ▲▲▲ */}

      {/* 患者リストのスクロールエリア */}
      <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}>
        {/* 【最重要】 space-y-3 から space-y-2 に変更してカード間の間隔を狭く */}
        <div className="flex flex-col p-4 space-y-2">
          {/* ▼▼▼ 【重要】AnimatePresenceでリスト全体を囲む ▼▼▼ */}
          <AnimatePresence>
            {patients.map((patient) => (
              <motion.button
                key={patient.id}
                onClick={() => handlePatientSelect(patient)}
                // 【追加】レイアウトの自動アニメーションを有効化
                layout
                // 【追加】登場時のアニメーション
                initial={{ opacity: 0, y: -20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                // 【追加】退出時のアニメーション（今回は使われませんが念のため）
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                                 className={`w-full p-2 rounded-lg border origin-top ${/* p-4 から p-2 へ変更 */
                   (context === 'settings' ? editingPatient?.id === patient.id : selectedPatientId === patient.id)
                     ? 'bg-green-50 border-green-200 text-green-700'
                     : 'bg-white border-gray-200 hover:bg-gray-50'
                 }`}
              >
                {/* ▼▼▼ 【重要】items-centerで垂直中央揃えに変更 ▼▼▼ */}
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User size={20} className="text-green-600" />
                  </div>
                  {/* ▼▼▼ この部分を修正 ▼▼▼ */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-gray-900 truncate">{patient.name}</div>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">
                      {patient.nameRomaji}
                    </div>
                  </div>
                  {/* ▲▲▲ ここまで修正 ▲▲▲ */}
                  <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
          {/* ▲▲▲ ここまで ▲▲▲ */}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div
        ref={allPatientSwipeRef}
        style={allPatientSwipeStyle}
        className={`fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] ${context === 'settings' ? 'z-[41]' : 'z-40'}`}
      >
        <motion.div
          initial={isNavigatingBack ? { x: '-100%', opacity: 1 } : { x: '100%', opacity: 1 }}
          animate={{ x: 0, opacity: 1 }}
          exit={
            isNavigatingBack
              ? { x: '100%', opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
              : { x: '-100%' }
          }
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="h-full w-full bg-white flex flex-col shadow-lg"
        >
          {content}
        </motion.div>
      </div>
    );
  }

  // パネル重なり順メモ
  // 設定ワークフロー: 一覧(30) > 編集(20) を担保
  // 診療フロー: 患者一覧(40) > 患者編集(20)
  const containerZ = context === 'settings' ? 'z-30' : 'z-40';
  const widthClass = context === 'settings' ? 'panel-col--settings' : 'panel-col--workflow';

  return (
    <motion.div
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`panel-col ${widthClass} ${containerZ} shadow-lg`}
    >
      {content}
    </motion.div>
  );
};
